<script lang="ts" setup>
import { i18n } from '@/i18n';
import SimpleCard from '@/components/atom/SimpleCard.vue';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { onMounted } from 'vue';
import { IDisplayObject, TableProperty, TableType, isCommonTableHeaderKey } from '@/coreDomainsModule/table';
import { TableContentViewModel } from '@/viewModels/tableContent';
import { useBaseTableContentStore } from './useBaseTableContentStore';
import { storeToRefs } from 'pinia';
import { TableContentItem } from './interface/tableContentItems';
import { BaseTableContentViewModel } from './baseTableContentViewModel';
import {
  TableContentHeader,
  TableContentProperty,
  isBusinessesForSupplierForecastHeaderKey,
  isForecastForReceiptKey,
  isForecastAccuracyKey,
  isOrderControlHeaderKey,
  isOrderHeaderKey,
  isUniqueHeaderKey,
  isUnmanagedBusinessHeaderKey,
  isTrashBinHeaderKey,
  isMoveWareHouseHeaderKey,
  isEditBusinessHeaderKey,
  OrderHeaderKey,
} from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { MAX_COLUMN_WIDTH, MIN_COLUMN_WIDTH, STICKY_COLUMN_WIDTH } from './constants';
import { ResizeStateService } from './services/resizeStateService';
import OrderTableHead from './components/order/OrderTableHead.vue';
import OrderTableCell from './components/order/OrderTableCell.vue';
import CommonTableHead from './components/CommonTableHead.vue';
import CommonTableCell from './components/CommonTableCell.vue';
import UniqueTableHead from './components/unique/UniqueTableHead.vue';
import UniqueTableCell from './components/unique/UniqueTableCell.vue';
import UnmanagedBusinessTableCell from './components/unmanagedBusiness/UnmanagedBusinessTableCell.vue';
import UnmanagedBusinessTableHead from './components/unmanagedBusiness/UnmanagedBusinessTableHead.vue';
import { NarrowState } from './interface/uiState';
import BusinessesForSupplierForecastTableHead from './components/businessesForSupplierForecast/BusinessesForSupplierForecastTableHead.vue';
import BusinessesForSupplierForecastTableCell from './components/businessesForSupplierForecast/BusinessesForSupplierForecastTableCell.vue';
import { LoadingState, isError, isLoading, isZeroMatch } from '@/coreDomainsModule/uiLoadingState';
import OrderControlTableCell from './components/orderControl/OrderControlTableCell.vue';
import OrderControlTableHead from './components/orderControl/OrderControlTableHead.vue';
import { LottieAnimation } from 'lottie-web-vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import SvgLoader from '@/utils/SvgLoader.vue';
import TrashBinTableHead from './components/trashBin/TrashBinTableHead.vue';
import TrashBinTableCell from './components/trashBin/TrashBinTableCell.vue';
import MoveWarehouseTableCell from './components/moveWarehouse/moveWarehouseTableCell.vue';
import EditBusinessTableCell from './components/editBusiness/EditBusinessTableCell.vue';
import CustomerForecastTableHeaderWrapper from './components/customerForecast/CustomerForecastTableHeaderWrapper.vue';
import CustomerForecastTableCellWrapper from './components/customerForecast/CustomerForecastTableCellWrapper.vue';
import ForecastAccuracyTableHead from './components/customerForecast/ForecastAccuracyTableHead.vue';
import ForecastAccuracyTableCell from './components/customerForecast/ForecastAccuracyTableCell.vue';
import { Order } from '@/coreDomainsModule/entities/order';
import { PartialDelivery } from '@/coreDomainsModule/entities/partialDelivery';
import TableCell from './components/TableCell.vue';

/**
 * LEANPATHで使うテーブルの大元のコンポーネント
 * もしtemplate部分が肥大化しそうになったら、v-if以下をコンポーネント化することを検討する
 */

// props
const props = withDefaults(
  defineProps<{
    propTableContentProperties: TableContentHeader;
    propDisplayObjects?: TableContentItem[];
    propLoadingStatus: LoadingState;
    propTableType: TableType;
    propIsEditing?: boolean;
    propIsReset?: boolean;
    propTableId?: string;
    propAllOfResult?: number;
    propCheckedIds?: Array<string>;
    propIsAllSelected?: boolean;
    propEditingId?: string;
  }>(),
  {
    propDisplayObjects: undefined,
    propIsEditing: false,
    propIsReset: false,
    propTableId: '', // NOTE: 同じTableTypeのテーブルが複数ある場合に使用する
    propAllOfResult: undefined,
    propCheckedIds: undefined, // テーブル全選択時に親コンポーネントからのid一覧を渡す際に使用
    propIsAllSelected: false,
    propEditingId: '',
  }
);

// emits
const emits = defineEmits<{
  (e: 'submit'): void;
  (e: 'complete', status: boolean): void;
  (e: 'open-excel-output-modal', event: Event, displayObj: IDisplayObject[]);
  (e: 'checked', checkedIds: string[]);
  (e: 'onClickVariableContent', targetItem?: TableProperty): void;
  (e: 'onClickSort', sortKey: TableContentProperty, isAsc: boolean): void;
  (e: 'onClickFilter', filterState: NarrowState[]): void;
  (e: 'reload'): void; // エラー発生時の再読み込み
  (e: 'onClickTrashBinAllSelect'): void;
  (e: 'onClickTransitionTimeline', displayId?: string): void;
  (e: 'onClickEdit', id: string): void;
  (e: 'onClickSave', id: string): void;
  (e: 'onChangeOrderProperty', orderId: string, property: OrderHeaderKey, value: string): void;
  (e: 'onChangePartialDelivery', orderId: string, partialDeliveryId: string, property: OrderHeaderKey, value: string): void;
  (e: 'onClickDelete', orderId: string, partialDeliveryId: string): void;
  (e: 'onClickAddPartialDelivery', orderId: string): void;
  (e: 'onClickSaveMemo', id: string, memoText: string): void;
  (e: 'onClickDeleteMemo', id: string): void;
  (e: 'onClickReminderMail', id: string): void;
  (e: 'onClickOrderBasis', id: string): void;
  (e: 'onClickDownload', displayId: string): void;
  (e: 'onClickTransactionList', displayId?: string): void;
  (e: 'onClickForecastRequestMail', displayId?: string): void;
  (e: 'onClickRemoveFromForecast', displayId: string): void;
}>();

//変数
const t = i18n.t;
const vm = new TableContentViewModel();
const baseTableContentVM = new BaseTableContentViewModel();
const store = useBaseTableContentStore(props.propTableType + props.propTableId)();
const { state } = storeToRefs(store);

const partialDeliveryAddLineWidth = ref(0);
const partialDeliveryAddLineRight = ref(0);

const {
  $reset,
  initializeTableContent,
  saveTableHeaderPreference,
  selectAllLines,
  selectAllLinesForOrder,
  setNarrowState,
  onSelectedLine,
  getModifiableHeaderList,
  onToggleDisplayHeader,
  selectAllLinesForBusinessId,
  onSelectedLineForBusinessId,
} = store;

onMounted(async () => {
  if (props.propDisplayObjects === undefined) return; // TODO: 例外処理
  $reset();
  window.addEventListener('scroll', closeAlert);
  initializeTableContent(
    props.propTableContentProperties,
    props.propDisplayObjects,
    props.propLoadingStatus,
    props.propTableType,
    props.propIsEditing
  );
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', closeAlert);
});

const useTableHeader = computed((): TableContentProperty[] => {
  if (state.value?.tableContentProperties === undefined) return [];
  return state.value.tableContentProperties.contentProperties;
});

const useItems = computed((): TableContentItem[] => {
  if (state.value?.items === undefined) return [];
  return state.value.items;
});

/**
 * テーブルの列幅をヘッダー順に並べた配列を返す
 */
const computedColumnWidths = computed((): string[] => {
  if (!state?.value?.tableContentProperties?.contentProperties) return [];
  return state.value.tableContentProperties.contentProperties.map((contentProperty, index) => {
    // 固定幅
    if (baseTableContentVM.isSticky(state.value.tableContentProperties.options.stableColumnCount, index)) {
      return contentProperty.headerWidth ? `${contentProperty.headerWidth}px` : `${STICKY_COLUMN_WIDTH}px`;
    }
    // 列幅を明示的に指定していない場合はmax-contentで調整されるようにする
    if (!contentProperty.headerWidth) return 'minmax(max-content, 1fr)';
    if (!contentProperty.isVariableWidth) return `${contentProperty.headerWidth}px`;
    const width = baseTableContentVM.clampedTableColumnWidth(contentProperty.headerWidth);
    // リサイズ中の列
    if (state.value.resizeState.resizingIndex === index) {
      return `${width}px`;
    }
    return `minmax(${width}px, ${width}fr)`;
  });
});

/**
 * OrderEstimateTimeOfArrivalヘッダーの左端がテーブルの右端からどれぐらいのWidthなのかを測る
 * @returns - Width
 */
const measurePartialDeliveryAddLineWidthMethod = (): [number, number] => {
  const table = document.getElementById(ResizeStateService.getTableContentId(props.propTableType));
  if (!table) {
    return [0, 0];
  }

  const startHeader = table.querySelector('th[data-key="OrderDeleteButton"]');
  if (!startHeader) {
    return [0, 0];
  }

  const endHeader = table.querySelector('th[data-key="OrderEditButton"]');
  if (!endHeader) {
    return [0, 0];
  }

  const tableRect = table.getBoundingClientRect();
  const startHeaderRect = startHeader.getBoundingClientRect();
  const endHeaderRect = endHeader.getBoundingClientRect();

  const width = endHeaderRect.right - startHeaderRect.left;
  const right = tableRect.right - endHeaderRect.right;
  return [width, right];
};

watch(
  () => props.propLoadingStatus,
  () => {
    state.value.loadingState = props.propLoadingStatus;
  }
);

watch(
  () => props.propIsEditing,
  () => {
    state.value.isEditing = props.propIsEditing;
  }
);

watch(
  () => [props.propDisplayObjects, props.propEditingId],

  async () => {
    if (props.propDisplayObjects) {
      state.value.items = props.propDisplayObjects;
    }
    if (props.propTableType === 'firmOrderTable') {
      await nextTick();
      [partialDeliveryAddLineWidth.value, partialDeliveryAddLineRight.value] = measurePartialDeliveryAddLineWidthMethod();
    }
  },
  { deep: true }
);

watch(
  () => props.propIsReset,
  (newVal) => {
    if (newVal) {
      $reset();

      initializeTableContent(
        props.propTableContentProperties,
        props.propDisplayObjects ?? [],
        props.propLoadingStatus,
        props.propTableType,
        props.propIsEditing
      );
    }
  }
);

watch(
  () => props.propIsAllSelected,
  (val) => {
    if (val) {
      state.value.selectedIds = props.propCheckedIds ?? [];
    }
  }
);

watch(
  () => props.propTableContentProperties,
  (newVal) => {
    saveTableHeaderPreference(newVal.contentProperties);
  }
);

/**
 * リサイザーのマウスダウンイベント
 * @param event - マウスイベント
 * @param index - インデックス
 */
const onMouseDownResizer = (event: MouseEvent, index: number) => {
  store.onMouseDownResizer(event, index);
};

/**
 * アラートを閉じる処理
 */
const closeAlert = () => {
  state.value.isOpeningAlertIndex = '';
};

/**
 * チェックボックス
 * @param displayId - 表示ID
 * @param isAll - 全選択
 */
const onCheckboxSelectedWrapper = (displayId: string, isAll = false) => {
  if (isAll) {
    selectAllLines(useItems.value);
  } else {
    onSelectedLine(displayId);
  }
  emits('checked', state.value.selectedIds); // onSelectedLineの結果を親コンポーネントに通知
};

/**
 * businessIdのチェックボックス
 * @param businessId - 取引ID
 * @param isAllSelected - 全選択
 * @param isAllClear - 全解除
 */
const onCheckboxSelectedBusinessId = (businessId: string, isAllSelected?: boolean, isAllClear?: boolean) => {
  if (isAllSelected !== undefined && isAllSelected) {
    selectAllLinesForBusinessId(useItems.value);
  } else if (isAllClear !== undefined && isAllClear) {
    state.value.isAllSelected = false;
    state.value.selectedIds = [];
  } else {
    onSelectedLineForBusinessId(businessId);
  }

  emits('checked', state.value.selectedIds); // onSelectedLineの結果を親コンポーネントに通知
};

/**
 * 発注むけのチェックボックス
 * @param displayId - 表示ID
 * @param isAll - 全選択
 */
const onCheckboxSelectedWrapperForOrderControl = (displayId: string, isAll = false) => {
  if (isAll) {
    selectAllLinesForOrder(useItems.value);
  } else {
    onSelectedLine(displayId);
  }
  emits('checked', state.value.selectedIds); // onSelectedLineの結果を親コンポーネントに通知
};

/**
 * テーブルの内容をソートする
 * @param index - インデックス
 * @param key - キー
 * @param isAsc - 昇順
 */
const sortTableContentItemsWrapper = async (index: number, key: TableContentProperty, isAsc: boolean) => {
  // selectedIdsが存在する場合、checkboxを全解除する
  if (state.value.selectedIds.length > 0) {
    state.value.isAllSelected = false;
    state.value.selectedIds = [];
    emits('checked', state.value.selectedIds); // onSelectedLineの結果を親コンポーネントに通知
  }
  emits('onClickSort', key, !isAsc);
  // ソートの矢印を切り変える
  state.value.tableContentProperties.contentProperties[index].sortOption.isAsc = !isAsc;
};

/**
 * narrowStateを発火させる
 * @param key - 絞り込むヘッダーのキー
 * @param value - 絞り込む値
 */
const setNarrowStateWrapper = async (key: TableContentProperty, value?: string | number) => {
  // TODO: valueを現状、itemから単純にキー指定で取得しているが、viewModelでパターン網羅して取得するように修正する（orderCellとかひとつも取れていない）

  // selectedIdsが存在する場合、checkboxを全解除する
  if (state.value.selectedIds.length > 0) {
    state.value.isAllSelected = false;
    state.value.selectedIds = [];
    emits('checked', state.value.selectedIds); // onSelectedLineの結果を親コンポーネントに通知
  }
  setNarrowState(key, value);
  emits('onClickFilter', state.value.narrowStates);
};

/**
 * itemが分納を持っているかどうかを判定する
 * @param item - テーブルのアイテム
 */
const hasPartialDelivery = (item: TableContentItem): boolean => {
  const order = item as Order;
  return order.partialDeliveries !== undefined;
};

/**
 * itemのbusinessId or aggregateBusinessIdがテーブルでチェックされた取引に含まれているかを判定する
 * @param item - テーブルのアイテム
 */
const isCheckedBusinessId = (item: TableContentItem) => {
  return state.value.selectedIds.includes(item?.businessId ?? item?.aggregateBusinessId);
};
</script>

<template>
  <!-- 各種テーブルに表示するための要素の集合 -->
  <SimpleCard :id="ResizeStateService.getTableContainerId(propTableType)" class="base-table-wrapper" @scroll="closeAlert">
    <table :id="ResizeStateService.getTableContentId(propTableType)" class="mb-2 base-table">
      <thead class="bg-gray-200">
        <!-- 列幅変更中にコンテンツがドラッグで選択されることがないように、userSelectを設定 -->
        <tr :style="{ userSelect: state.resizeState.resizingIndex !== -1 ? 'none' : 'auto' }">
          <template v-for="(header, headerIndex) in useTableHeader" :key="`${propTableType}-header-` + headerIndex">
            <template v-if="isCommonTableHeaderKey(header.key)">
              <CommonTableHead
                :key="headerIndex"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth"
                :is-variable-width="header.isVariableWidth"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :is-editing="state.isEditing"
                :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                :is-all-selected="state.isAllSelected"
                :modifiable-header-list="getModifiableHeaderList(propTableType, propTableContentProperties)"
                :is-open-header-modify-button-menu="state.isOpenHeaderModifyButtonMenu"
                :table-type="propTableType"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
                @on-checkbox-selected="onCheckboxSelectedWrapper"
                @on-toggle-display-header="(key: string, isHide: boolean) => {onToggleDisplayHeader(propTableType, key, propTableContentProperties.contentProperties, isHide)}"
                @on-checkbox-selected-business-id="onCheckboxSelectedBusinessId('', !state.isAllSelected, state.isAllSelected)"
                @on-toggle-header-modify-button-menu="
                  (isOpen) => {
                    state.isOpenHeaderModifyButtonMenu = isOpen;
                  }
                "
              />
            </template>

            <template v-if="isUniqueHeaderKey(header.key)">
              <UniqueTableHead
                :header="header"
                :header-key="header.key"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
              />
            </template>

            <template v-if="isOrderHeaderKey(header.key)">
              <OrderTableHead
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :is-order-group-edit-mode="props.propIsEditing"
                :is-editing="!!props.propEditingId"
                :editing-id="props.propEditingId"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
              />
            </template>

            <template v-if="isOrderControlHeaderKey(header.key)">
              <OrderControlTableHead
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :is-editing="state.isEditing"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
                @on-checkbox-selected="onCheckboxSelectedWrapperForOrderControl"
              />
            </template>

            <template v-if="isForecastForReceiptKey(header.key)">
              <CustomerForecastTableHeaderWrapper
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
              />
            </template>

            <template v-if="isForecastAccuracyKey(header.key)">
              <ForecastAccuracyTableHead
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
              />
            </template>

            <template v-if="isUnmanagedBusinessHeaderKey(header.key)">
              <UnmanagedBusinessTableHead
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
              />
            </template>

            <template v-if="isBusinessesForSupplierForecastHeaderKey(header.key)">
              <BusinessesForSupplierForecastTableHead
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
              />
            </template>

            <template v-if="isTrashBinHeaderKey(header.key)">
              <TrashBinTableHead
                :class="[
                  baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, headerIndex) ? 'sticky-content' : '',
                ]"
                :style="{
                  left: baseTableContentVM.calcStickyDiff(
                    state.tableContentProperties.options.stableColumnCount,
                    headerIndex,
                    state.accumulationTableContentWidth[headerIndex]
                  ),
                }"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                :is-variable-width="header.isVariableWidth"
                :is-all-selected="state.isAllSelected"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
                @on-checkbox-selected="onCheckboxSelectedWrapper"
              />
            </template>

            <template v-if="isMoveWareHouseHeaderKey(header.key)">
              <CommonTableHead
                :key="headerIndex"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth"
                :is-variable-width="header.isVariableWidth"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :is-editing="state.isEditing"
                :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                :is-all-selected="state.isAllSelected"
                :modifiable-header-list="getModifiableHeaderList(propTableType, propTableContentProperties)"
                :is-open-header-modify-button-menu="state.isOpenHeaderModifyButtonMenu"
                :table-type="propTableType"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
                @on-checkbox-selected="onCheckboxSelectedWrapper"
                @on-toggle-display-header="(key: string, isHide: boolean) => {onToggleDisplayHeader(propTableType, key, propTableContentProperties.contentProperties, isHide)}"
                @on-toggle-header-modify-button-menu="
                  (isOpen) => {
                    state.isOpenHeaderModifyButtonMenu = isOpen;
                  }
                "
              />
            </template>
            <template v-if="isEditBusinessHeaderKey(header.key)">
              <CommonTableHead
                :key="headerIndex"
                :header="header"
                :header-index="headerIndex"
                :accumulation-table-content-width="state.accumulationTableContentWidth"
                :is-variable-width="header.isVariableWidth"
                :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                :is-editing="state.isEditing"
                :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                :is-all-selected="state.isAllSelected"
                :modifiable-header-list="getModifiableHeaderList(propTableType, propTableContentProperties)"
                :is-open-header-modify-button-menu="state.isOpenHeaderModifyButtonMenu"
                :table-type="propTableType"
                @on-click-sort="sortTableContentItemsWrapper"
                @on-mousedown-resizer="onMouseDownResizer"
                @on-checkbox-selected="onCheckboxSelectedWrapper"
                @on-toggle-display-header="(key: string, isHide: boolean) => {onToggleDisplayHeader(propTableType, key, propTableContentProperties.contentProperties, isHide)}"
                @on-toggle-header-modify-button-menu="
                  (isOpen) => {
                    state.isOpenHeaderModifyButtonMenu = isOpen;
                  }
                "
              />
            </template>
          </template>
        </tr>
      </thead>
      <tbody>
        <template v-if="isLoading(state.loadingState.status)">
          <tr>
            <td class="loading-info" colspan="1000">
              <div class="loading-info__content">
                <LottieAnimation
                  v-if="isLoading(state.loadingState.status)"
                  ref="anim"
                  :animation-data="loadingAnimation"
                  :auto-play="true"
                  :loop="true"
                  :speed="1"
                />
                <SvgLoader v-else :class="`modal__icon`" name="" />
                <h1 class="loading-info__title">{{ t('データを読み込んでいます') }}</h1>
              </div>
            </td>
          </tr>
        </template>
        <template v-else-if="isZeroMatch(state.loadingState.status)">
          <tr>
            <td class="no-data" colspan="1000">
              {{ t('該当する取引はありません。') }}
            </td>
          </tr>
        </template>
        <template v-else-if="isError(state.loadingState.status)">
          <tr>
            <td class="error" colspan="1000">
              {{ t('情報取得に失敗しました。時間を置いて再施行してください。') }}
              <button class="error__button" @click="() => emits('reload')">
                {{ t('再読込') }}
              </button>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr v-if="state.isAllSelected && propTableType === 'trash'">
            <td colspan="1000" class="trash-bin-all-selected__content">
              <div>
                {{ t('このページ内の取引全てが選択されています。') }}
              </div>
              <button class="trash-bin-all-selected__button" @click="emits('onClickTrashBinAllSelect')">
                {{ t('取引') }}{{ propAllOfResult }}{{ t('件全て選択する') }}
              </button>
            </td>
          </tr>
          <template v-for="(item, itemIndex) in useItems" :key="`${propTableType}-content-` + itemIndex">
            <tr>
              <template v-for="(header, headerIndex) in useTableHeader" :key="propTableType + header + headerIndex">
                <template v-if="isCommonTableHeaderKey(header.key)">
                  <CommonTableCell
                    :header="header"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :item="item"
                    :is-editing="state.isEditing"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    :selected-ids="state.selectedIds"
                    :narrow-states="state.narrowStates"
                    :is-expanded="false"
                    :table-type="propTableType"
                    @on-checkbox-selected="onCheckboxSelectedWrapper"
                    @on-checkbox-selected-business-id="onCheckboxSelectedBusinessId"
                    @set-narrow-state="setNarrowStateWrapper(header, item?.[header.key])"
                  />
                </template>

                <template v-if="isUniqueHeaderKey(header.key)">
                  <UniqueTableCell
                    :header="header"
                    :header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-editing="state.isEditing"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                  />
                </template>

                <template v-if="isOrderHeaderKey(header.key)">
                  <OrderTableCell
                    :header="header"
                    :order-header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :item-index="itemIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-alert-opening="itemIndex.toString() === state.isOpeningAlertIndex"
                    :editing-id="props.propEditingId"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    :is-order-group-edit-mode="props.propIsEditing"
                    @change-editable-value="(headerKey, value) => emits('onChangeOrderProperty', item?.orderId, headerKey, value)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                    @click-alert="
                      () => {
                        state.isOpeningAlertIndex = itemIndex.toString();
                      }
                    "
                    @on-click-edit="emits('onClickEdit', $event)"
                    @on-click-save="emits('onClickSave', $event)"
                    @on-click-save-memo="(id, memoText) => emits('onClickSaveMemo', id, memoText)"
                    @on-click-delete-memo="emits('onClickDeleteMemo', $event)"
                    @on-click-reminder-mail="emits('onClickReminderMail', $event)"
                    @on-click-order-basis="emits('onClickOrderBasis', $event)"
                  />
                </template>

                <template v-if="isOrderControlHeaderKey(header.key)">
                  <OrderControlTableCell
                    :header="header"
                    :order-control-header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-editing="state.isEditing"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                    @on-checkbox-selected="onCheckboxSelectedWrapperForOrderControl"
                  />
                </template>
                <template v-if="isForecastForReceiptKey(header.key)">
                  <CustomerForecastTableCellWrapper
                    :header="header"
                    :header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                    @on-click-transition-timeline="emits('onClickTransitionTimeline', item?.displayId)"
                    @on-click-transaction-list="emits('onClickTransactionList', item?.displayId)"
                    @on-click-download="emits('onClickDownload', item?.displayId)"
                    @on-click-forecast-request-mail="emits('onClickForecastRequestMail', item?.displayId)"
                    @on-click-edit="emits('onClickEdit', item?.displayId)"
                    @on-click-remove-from-forecast="emits('onClickRemoveFromForecast', item?.displayId)"
                  />
                </template>

                <template v-if="isForecastAccuracyKey(header.key)">
                  <ForecastAccuracyTableCell
                    :header="header"
                    :header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                    @on-click-download="(displayId) => emits('onClickDownload', displayId)"
                  />
                </template>

                <template v-if="isUnmanagedBusinessHeaderKey(header.key)">
                  <UnmanagedBusinessTableCell
                    :header="header"
                    :unmanged-business-header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-editing="state.isEditing"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                  />
                </template>

                <template v-if="isBusinessesForSupplierForecastHeaderKey(header.key)">
                  <BusinessesForSupplierForecastTableCell
                    :header="header"
                    :header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :forecast-start-year-month="state.tableContentProperties.forecastStartYearMonth"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                  />
                </template>

                <template v-if="isTrashBinHeaderKey(header.key)">
                  <TrashBinTableCell
                    :header="header"
                    :trash-bin-header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                    @on-checkbox-selected="onCheckboxSelectedWrapper"
                  />
                </template>

                <template v-if="isMoveWareHouseHeaderKey(header.key)">
                  <MoveWarehouseTableCell
                    :header="header"
                    :move-warehouse-header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-editing="state.isEditing"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                  />
                </template>

                <template v-if="isEditBusinessHeaderKey(header.key)">
                  <EditBusinessTableCell
                    :header="header"
                    :edit-business-header-key="header.key"
                    :item="item"
                    :header-index="headerIndex"
                    :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                    :is-variable-width="header.isVariableWidth"
                    :is-all-selected="state.isAllSelected"
                    :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                    :is-editing="state.isEditing"
                    :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                    :is-checked="state.selectedIds.includes(item?.displayId)"
                    :is-checked-business-id="isCheckedBusinessId(item)"
                    @on-click-narrow="setNarrowStateWrapper(header, item?.[header.key])"
                  />
                </template>
              </template>
            </tr>
            <!-- 発注テーブル向け機能：分納がある場合は表示する -->
            <template v-if="hasPartialDelivery(item)">
              <template
                v-for="(partialDelivery, partialDeliveryIndex) in item.partialDeliveries"
                :key="`${itemIndex}-${partialDeliveryIndex}`"
              >
                <tr>
                  <template
                    v-for="(header, headerIndex) in useTableHeader"
                    :key="propTableType + header + headerIndex + itemIndex + partialDeliveryIndex"
                  >
                    <template v-if="isOrderHeaderKey(header.key)">
                      <OrderTableCell
                        is-partial-delivery
                        :header="header"
                        :order-header-key="header.key"
                        :item="partialDelivery"
                        :header-index="headerIndex"
                        :item-index="partialDeliveryIndex"
                        :accumulation-table-content-width="state.accumulationTableContentWidth[headerIndex]"
                        :is-variable-width="header.isVariableWidth"
                        :is-all-selected="state.isAllSelected"
                        :stable-column-count="state.tableContentProperties.options.stableColumnCount"
                        :is-bold="baseTableContentVM.isBold(state.narrowStates, header?.propertyPath?.paths)"
                        :is-alert-opening="partialDeliveryIndex.toString() === state.isOpeningAlertIndex"
                        :editing-id="props.propEditingId"
                        :is-checked="state.selectedIds.includes(item?.displayId)"
                        :is-checked-business-id="state.selectedIds.includes(item?.businessId)"
                        @change-editable-value="
                          (headerKey, value) => emits('onChangePartialDelivery', item.orderId, partialDelivery.id, headerKey, value)
                        "
                        @on-click-narrow="setNarrowStateWrapper(header, partialDelivery?.[header.key])"
                        @click-alert="
                          () => {
                            state.isOpeningAlertIndex = partialDeliveryIndex.toString();
                          }
                        "
                        @on-click-edit="emits('onClickEdit', $event)"
                        @on-click-save="emits('onClickSave', $event)"
                        @on-click-delete="emits('onClickDelete', item.orderId, partialDelivery.id)"
                      />
                    </template>
                    <template v-else>
                      <!-- 何も表示しない-->
                      <TableCell />
                    </template>
                  </template>
                </tr>
              </template>
              <!-- 分納を追加するボタン -->
              <!-- 編集中にしか表示しない -->
              <tr v-show="item.partialDeliveries?.length > 0 && item.orderId === propEditingId">
                <td class="add-partial-delivery" :colspan="1000">
                  <div class="add-partial-delivery__container">
                    <button class="add-partial-delivery__button" @click.stop.prevent="emits('onClickAddPartialDelivery', item.orderId)">
                      {{ t('一行追加') }}<SvgLoader class="add-partial-delivery__button__icon" name="plusButtonIcon" />
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </template>
      </tbody>
    </table>
  </SimpleCard>
</template>

<style lang="scss" scoped>
/* 一部、scss/tableContent/tableContent.scssに置いてある */

// 画面の縦幅がbaseHeightを超えた場合、テーブルの高さをレスポンシブに変更する
@media screen and (min-height: $baseHeight) {
  .base-table-wrapper {
    min-height: $baseTableResponsiveHeight;
    max-height: $baseTableResponsiveHeight;
  }
}
// 画面の縦幅がbaseHeight以下の場合、テーブルの高さをbaseHeightで固定する
@media screen and (max-height: $baseHeight) {
  .base-table-wrapper {
    min-height: $baseTableMinHeight;
    max-height: $baseTableMinHeight;
  }
}

.base-table-wrapper {
  overflow-x: auto;
}

// tableの要素をgridで管理するために必要
table {
  table-layout: fixed;
  display: grid;
  grid-template-columns: v-bind('computedColumnWidths.join(" ")');
}

thead,
tbody {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

thead {
  position: sticky;
  top: 0;
  z-index: 5;
}
tr {
  display: contents;
}

:deep(th),
:deep(td) {
  white-space: nowrap;
  display: block;
  max-width: v-bind('`${MAX_COLUMN_WIDTH}px`');
}

:deep(th) {
  background-color: $surfaceSecondary;
}

:deep(tr > th),
:deep(tr > td) {
  align-self: stretch;
}

:deep(th.sticky-content) {
  z-index: 6;
}

.forecast-status {
  &__icon {
    margin: auto;
  }
}

.virtual-background {
  /* TODO: 効いてないので調査 */
  background-color: $backgroundOver;
}

.expand-button {
  display: grid;
  height: inherit;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.not-exists-button-size {
  display: inline-block;
  height: 22px;
}

// ローディングアイコン
.dli-loading-1 {
  display: inline-block;
  vertical-align: middle;
  color: #666;
  line-height: 1;
  width: 4em;
  height: 4em;
  border: 0.12em solid currentColor;
  border-top-color: rgba(102, 102, 102, 0.3);
  border-radius: 50%;
  box-sizing: border-box;
  animation: rotate 1s linear infinite;
}

.no-data {
  background-color: $surfaceDefault;
  color: $textSecondary;
  height: 477px;

  font-family: Noto Sans JP;
  font-size: 14px;
  font-weight: 400;
  line-height: 18.2px;
  text-align: center;

  padding: 0;

  grid-column: 1 / -1;

  max-width: none;

  display: grid;
  place-items: center center;
}

.error {
  background-color: $semanticErrorSubdued;
  height: 477px;

  padding: 0;

  grid-column: 1 / -1;

  max-width: none;

  display: grid;
  place-items: center center;

  color: $semanticError;
  font-family: Noto Sans JP;
  font-size: 12px;
  font-weight: 400;
  line-height: 12px;
  text-align: center;

  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, auto);
  justify-content: center;

  &__button {
    color: $semanticPrimary;
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    text-align: center;

    text-decoration: underline;
  }
}

.loading-info {
  height: 477px;
  padding: 0;
  grid-column: 1 / -1;
  max-width: none;
  display: grid;
  place-items: center;

  &__content {
    display: grid;
    place-items: center;
  }
}

.trash-bin-all-selected {
  &__content {
    background: $surfaceDefault;
    height: 100%;
    padding-top: 10px;
    padding-bottom: 10px;
    max-width: none;

    display: grid;
    grid-column: 1 / -1;
    place-items: center center;

    font-size: 14px;
    font-weight: 400;
    text-align: center;

    gap: 64px;
    grid-template-columns: repeat(2, auto);
    justify-content: center;
  }
  &__button {
    color: $semanticPrimary;
    border-bottom: 1px solid;
  }
}

.add-partial-delivery {
  padding: 0;
  grid-column: 1 / -1;
  max-width: none;
  display: grid;
  place-items: center;
  justify-items: end;
  height: 3px;

  &__container {
    width: v-bind('`${partialDeliveryAddLineWidth}px`');
    right: v-bind('`${partialDeliveryAddLineRight}px`');
    border-top: 3px solid $semanticPrimary;
    box-sizing: unset;
    height: 0px;
    transform: translateY(-3px);
    z-index: 2;

    overflow: visible;
    position: relative;
  }

  &__button {
    /* 親要素に対して絶対配置し、中央に置く */
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate(-50%, -50%);

    /* ボタンを横並びにして中央揃え */
    display: inline-flex;
    align-items: center;

    /* ボタンの背景色や文字色など */
    background-color: $semanticPrimary;
    color: white;
    border: none;
    border-radius: 9999px; /* 角を丸くする */

    /* ボタン内余白 */
    padding: 0.5em 1em;
    font-size: 16px;
    cursor: pointer;

    /* 書式 */
    font-family: Noto Sans JP;
    font-weight: 700;
    font-size: 14px;
    line-height: 14px;
    letter-spacing: 0%;
    text-align: center;

    /* シャドウ */
    box-shadow: 0px 0px 12px 0px #4dbde9;

    /* ホバー時のエフェクト */
    transition: background-color 0.2s ease-in-out;

    &__icon {
      margin-left: 0.5em;
      display: inline-block;
      width: 1.5em;
      height: 1.5em;
      border-radius: 50%;
      color: #58a8f6;
      font-weight: bold;
      text-align: center;
      line-height: 1.5em;
    }
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
